import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { ChannelBindingPanel } from "../components/channel-binding-panel";
import { TestAgentPanel } from "../components/test-agent-panel";
import { useToast } from "../components/ui/toast";
import { ArrowLeft, Trash2 } from "lucide-react";

const formSchema = z.object({
  systemPrompt: z.string().min(1, "Обязательное поле").max(10000),
  active: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

type Tab = "settings" | "channels" | "test";

export function FunnelEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>("settings");
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);

  const funnel = useQuery({
    queryKey: ["funnel", id],
    queryFn: () => api.getFunnel(id!),
    enabled: !!id,
  });
  const statuses = useQuery({
    queryKey: ["pipeline-statuses", funnel.data?.amoPipelineId],
    queryFn: () => api.listPipelineStatuses(funnel.data!.amoPipelineId),
    enabled: !!funnel.data?.amoPipelineId,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      systemPrompt: "",
      active: true,
    },
  });

  useEffect(() => {
    if (funnel.data) {
      form.reset({
        systemPrompt: funnel.data.systemPrompt,
        active: funnel.data.active,
      });
      setSelectedStatusIds(funnel.data.amoStatusIds);
    }
  }, [funnel.data, form]);

  const update = useMutation({
    mutationFn: (data: FormValues) =>
      api.updateFunnel(id!, {
        systemPrompt: data.systemPrompt,
        active: data.active,
        amoStatusIds: selectedStatusIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      queryClient.invalidateQueries({ queryKey: ["funnels"] });
      toast("Настройки сохранены", "success");
    },
    onError: (err: Error) => {
      toast(err.message, "destructive");
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteFunnel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["funnels"] });
      queryClient.invalidateQueries({ queryKey: ["account"] });
      toast("Воронка удалена", "success");
      navigate("/settings");
    },
    onError: (err: Error) => {
      toast(err.message, "destructive");
    },
  });

  if (funnel.isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (funnel.error) {
    return (
      <div className="p-4 text-destructive">
        Ошибка: {funnel.error.message}
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "settings", label: "Настройки" },
    { key: "channels", label: "Каналы" },
    { key: "test", label: "Тест" },
  ];

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/settings">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-lg font-semibold">{funnel.data?.name}</h1>
        </div>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            if (confirm("Удалить воронку?")) remove.mutate();
          }}
          disabled={remove.isPending}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Удалить
        </Button>
      </div>

      <div className="flex gap-1 rounded-lg bg-muted p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "settings" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Настройки</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={form.handleSubmit((data) => update.mutate(data))}
              className="space-y-4"
            >
              <div className="space-y-1">
                <Label>Воронка amoCRM</Label>
                <p className="text-sm text-muted-foreground">
                  {funnel.data?.name}
                </p>
              </div>

              <div className="space-y-2">
                <div>
                  <Label>Этапы, где агент отвечает</Label>
                  <p className="text-xs text-muted-foreground">
                    Если ничего не выбрать, агент будет отвечать на всех этапах.
                  </p>
                </div>
                {statuses.isLoading && (
                  <div className="text-sm text-muted-foreground">
                    Загружаем этапы...
                  </div>
                )}
                {statuses.error && (
                  <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                    Не удалось загрузить этапы. Сохранение промпта и активности доступно.
                  </div>
                )}
                {statuses.data && statuses.data.length > 0 && (
                  <div className="space-y-2 rounded border p-3">
                    {statuses.data.map((status) => (
                      <label
                        key={status.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selectedStatusIds.includes(status.id)}
                          onChange={(e) => {
                            setSelectedStatusIds((current) =>
                              e.target.checked
                                ? [...current, status.id]
                                : current.filter((statusId) => statusId !== status.id),
                            );
                          }}
                        />
                        <span
                          className="h-3 w-3 rounded-full border"
                          style={{ backgroundColor: status.color ?? "transparent" }}
                        />
                        <span>{status.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {statuses.data && statuses.data.length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    У этой воронки нет доступных этапов.
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="systemPrompt">Системный промпт</Label>
                <Textarea
                  id="systemPrompt"
                  rows={8}
                  {...form.register("systemPrompt")}
                />
                {form.formState.errors.systemPrompt && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.systemPrompt.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Активен</Label>
                  <p className="text-xs text-muted-foreground">
                    Агент обрабатывает сообщения
                  </p>
                </div>
                <Switch
                  checked={form.watch("active")}
                  onCheckedChange={(val) => form.setValue("active", val)}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={update.isPending}
              >
                {update.isPending ? "Сохранение..." : "Сохранить"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {activeTab === "channels" && funnel.data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Каналы</CardTitle>
          </CardHeader>
          <CardContent>
            <ChannelBindingPanel
              funnelId={funnel.data.id}
              channels={funnel.data.channels}
            />
          </CardContent>
        </Card>
      )}

      {activeTab === "test" && funnel.data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Тест агента</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <TestAgentPanel funnelId={funnel.data.id} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
